import type { Hazard, ItemBox, ItemEvent, ItemId, Projectile } from '../core/types';
import type { Kart } from './kart';
import type { Track } from './track';
import { Rng } from '../core/rng';
import { clamp, dist2, TAU } from '../core/math';
import { rollItemId } from '../data/items';

/** Dobozok: sugár és visszatöltődés (mp). */
export const BOX_RADIUS = 26;
export const BOX_RESPAWN = 4;

/** Rulett hossza (mp). */
export const ROLL_TIME = 1.1;

/** Csapdák: a kart mögött ennyivel, élettartam 25 mp. */
export const DROP_OFFSET = 55;
export const OIL_RADIUS = 26;
export const OIL_LIFE = 25;
export const GLUE_RADIUS = 34;
export const GLUE_LIFE = 25;

/** Szódásüveg lövedék. */
export const SODA_OFFSET = 40;
export const SODA_SPEED = 620;
export const SODA_LIFE = 5;
export const SODA_RADIUS = 15;
/** A tulajdonos ennyi ideig nem találhatja el saját magát. */
export const SODA_OWNER_GRACE = 0.35;

/** Hatások. */
export const TURBO_TIME = 1.6;
export const TURBO_STRENGTH = 1.2;
export const SHIELD_TIME = 5;
export const LIGHTNING_SLOW = 0.5;
export const LIGHTNING_TIME = 2.2;
export const OIL_SPIN = 1.1;
export const GLUE_SLOW = 0.55;
export const GLUE_SLOW_TIME = 2.2;
export const SODA_HIT_SPIN = 1.0;

/**
 * Item-rendszer: dobozok, rulett, használat, lövedékek, csapdák.
 * Csak a kart item-mezőit és a felsorolt hatásokat (boost/slow/spin/shield) írja.
 * Minden véletlen a konstruktorban kapott rng-ből jön.
 */
export class ItemSystem {
  readonly track: Track;
  readonly rng: Rng;
  boxes: ItemBox[] = [];
  hazards: Hazard[] = [];
  projectiles: Projectile[] = [];

  /** folyamatos id-osztó a dobozoknak / csapdáknak / lövedékeknek */
  private nextId = 1;
  /** dobozonkénti dekoratív fázis; a reset ugyanezt állítja vissza */
  private readonly boxPhases: number[] = [];

  constructor(track: Track, seed: number) {
    this.track = track;
    this.rng = new Rng(seed);
    const positions = track.itemBoxPositions();
    for (let i = 0; i < positions.length; i++) this.boxPhases.push(this.rng.range(0, TAU));
    this.reset();
  }

  /** doboz-felvétel, rulett, lövedékek, csapdák, doboz-visszatöltés */
  update(dt: number, karts: Kart[]): ItemEvent[] {
    const events: ItemEvent[] = [];
    if (!(dt > 0)) return events;

    // 1. dobozok visszatöltése
    for (const box of this.boxes) {
      if (box.active) continue;
      box.respawn -= dt;
      if (box.respawn <= 0) {
        box.respawn = 0;
        box.active = true;
      }
    }

    // 2. lejárt rulett -> a tárgy a kartra kerül
    for (const kart of karts) {
      const target = kart.itemRollTarget;
      if (target !== null && kart.itemRollTimer <= 0) {
        kart.item = target;
        kart.itemRollTarget = null;
        events.push({ kind: 'roll', item: target, at: { x: kart.pos.x, y: kart.pos.y }, ownerId: kart.id });
      }
    }

    // 3. doboz-felvétel (a kart közepe és a doboz közepe alapján)
    for (const box of this.boxes) {
      if (!box.active) continue;
      for (const kart of karts) {
        if (kart.itemRollTimer > 0) continue; // már pörög, nem indítunk újat
        const reach = kart.radius + BOX_RADIUS;
        if (dist2(kart.pos, box.pos) > reach * reach) continue;

        const ratio = this.positionRatio(kart, karts);
        box.active = false;
        box.respawn = BOX_RESPAWN;
        const item = this.beginRoll(kart, ratio);
        events.push({ kind: 'pickup', item, at: { x: box.pos.x, y: box.pos.y }, ownerId: kart.id });
        break; // a doboz elfogyott
      }
    }

    // 4. lövedékek
    for (let i = this.projectiles.length - 1; i >= 0; i--) {
      const p = this.projectiles[i];
      p.pos.x += p.vel.x * dt;
      p.pos.y += p.vel.y * dt;
      p.life -= dt;
      const age = SODA_LIFE - p.life;

      if (this.track.surfaceAt(p.pos).outside) {
        p.spent = true;
        events.push({ kind: 'explode', item: p.kind, at: { x: p.pos.x, y: p.pos.y }, ownerId: p.ownerId });
        this.projectiles.splice(i, 1);
        continue;
      }

      let struck = false;
      for (const kart of karts) {
        if (kart.id === p.ownerId && age < SODA_OWNER_GRACE) continue;
        const reach = kart.radius + p.radius;
        if (dist2(kart.pos, p.pos) > reach * reach) continue;

        if (kart.shieldTimer > 0) {
          events.push({
            kind: 'blocked',
            item: p.kind,
            at: { x: kart.pos.x, y: kart.pos.y },
            ownerId: p.ownerId,
            targetId: kart.id,
          });
        } else {
          kart.spinOut(SODA_HIT_SPIN);
          events.push({
            kind: 'hit',
            item: p.kind,
            at: { x: kart.pos.x, y: kart.pos.y },
            ownerId: p.ownerId,
            targetId: kart.id,
          });
        }
        p.spent = true;
        struck = true;
        break;
      }

      if (struck || p.life <= 0) this.projectiles.splice(i, 1);
    }

    // 5. csapdák
    for (let i = this.hazards.length - 1; i >= 0; i--) {
      const h = this.hazards[i];
      h.life -= dt;
      if (h.life <= 0) {
        this.hazards.splice(i, 1);
        continue;
      }

      let sprung = false;
      for (const kart of karts) {
        const reach = kart.radius + h.radius;
        if (dist2(kart.pos, h.pos) > reach * reach) continue;

        if (kart.shieldTimer > 0) {
          events.push({
            kind: 'blocked',
            item: h.kind,
            at: { x: h.pos.x, y: h.pos.y },
            ownerId: h.ownerId,
            targetId: kart.id,
          });
        } else if (h.kind === 'oil') {
          kart.spinOut(OIL_SPIN);
          events.push({
            kind: 'hit',
            item: h.kind,
            at: { x: h.pos.x, y: h.pos.y },
            ownerId: h.ownerId,
            targetId: kart.id,
          });
        } else {
          kart.applySlow(GLUE_SLOW, GLUE_SLOW_TIME);
          events.push({
            kind: 'hit',
            item: h.kind,
            at: { x: h.pos.x, y: h.pos.y },
            ownerId: h.ownerId,
            targetId: kart.id,
          });
        }
        h.spent = true;
        sprung = true;
        break;
      }

      if (sprung) this.hazards.splice(i, 1); // egyszer sül el
    }

    return events;
  }

  /** a kart.item használata; a tárgy elfogy */
  use(kart: Kart, karts: Kart[]): ItemEvent[] {
    const item = kart.item;
    if (item === null) return [];

    kart.item = null;
    kart.itemRollTarget = null;
    kart.itemRollTimer = 0;

    const events: ItemEvent[] = [
      { kind: 'use', item, at: { x: kart.pos.x, y: kart.pos.y }, ownerId: kart.id },
    ];

    const fx = Math.cos(kart.heading);
    const fy = Math.sin(kart.heading);

    switch (item) {
      case 'turbo':
        kart.applyBoost(TURBO_TIME, TURBO_STRENGTH);
        break;

      case 'shield':
        kart.shieldTimer = SHIELD_TIME;
        break;

      case 'oil':
        this.addHazard('oil', kart.pos.x - fx * DROP_OFFSET, kart.pos.y - fy * DROP_OFFSET, OIL_RADIUS, kart.id, OIL_LIFE);
        break;

      case 'glue':
        this.addHazard('glue', kart.pos.x - fx * DROP_OFFSET, kart.pos.y - fy * DROP_OFFSET, GLUE_RADIUS, kart.id, GLUE_LIFE);
        break;

      case 'soda':
        this.projectiles.push({
          id: this.nextId++,
          kind: 'soda',
          pos: { x: kart.pos.x + fx * SODA_OFFSET, y: kart.pos.y + fy * SODA_OFFSET },
          vel: { x: fx * SODA_SPEED, y: fy * SODA_SPEED },
          radius: SODA_RADIUS,
          ownerId: kart.id,
          life: SODA_LIFE,
          spent: false,
        });
        break;

      case 'lightning':
        for (const other of karts) {
          if (other === kart) continue;
          if (other.shieldTimer > 0) {
            events.push({
              kind: 'blocked',
              item,
              at: { x: other.pos.x, y: other.pos.y },
              ownerId: kart.id,
              targetId: other.id,
            });
            continue;
          }
          other.applySlow(LIGHTNING_SLOW, LIGHTNING_TIME);
        }
        break;
    }

    return events;
  }

  /** a világ hívja, amikor egy kart felvesz egy dobozt (a világ is detektálhat) */
  startRoll(kart: Kart, positionRatio: number): void {
    if (kart.itemRollTimer > 0) return; // már pörög, nem indítunk újat
    this.beginRoll(kart, positionRatio);
  }

  reset(): void {
    const positions = this.track.itemBoxPositions();
    this.boxes = positions.map((pos, i) => ({
      id: i + 1,
      pos: { x: pos.x, y: pos.y },
      active: true,
      respawn: 0,
      phase: this.boxPhases[i] ?? 0,
    }));
    this.hazards = [];
    this.projectiles = [];
    this.nextId = this.boxes.length + 1;
  }

  /** 0 = élen, 1 = hátul; a teljesítmény alapján */
  private positionRatio(kart: Kart, karts: Kart[]): number {
    if (karts.length <= 1) return 0;
    let ahead = 0;
    for (const other of karts) {
      if (other !== kart && other.totalProgress > kart.totalProgress) ahead++;
    }
    return clamp(ahead / (karts.length - 1), 0, 1);
  }

  private beginRoll(kart: Kart, positionRatio: number): ItemId {
    const item = rollItemId(positionRatio, this.rng);
    kart.itemRollTarget = item;
    kart.itemRollTotal = ROLL_TIME;
    kart.itemRollTimer = ROLL_TIME;
    return item;
  }

  private addHazard(kind: ItemId, x: number, y: number, radius: number, ownerId: number, life: number): void {
    this.hazards.push({ id: this.nextId++, kind, pos: { x, y }, radius, ownerId, life, spent: false });
  }
}
